import { supabase } from '../utils/supabaseClient';
import { getCurrentUser } from '../utils/supabaseClient';

/**
 * Get all projects for the current user
 * @returns {Promise<Object>} - Object with data and error properties
 */
export const getProjects = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) return { data: [], error: 'User not authenticated' };

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    
    return { data, error };
  } catch (error) {
    console.error('Error fetching projects:', error);
    return { data: [], error: error.message };
  }
};

/**
 * Get a single project by ID
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Object with data and error properties
 */
export const getProject = async (projectId) => {
  try {
    const user = await getCurrentUser();
    if (!user) return { data: null, error: 'User not authenticated' };

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .eq('user_id', user.id)
      .single();
    
    return { data, error };
  } catch (error) {
    console.error('Error fetching project:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Save a new project
 * @param {Object} projectData - Project data to save
 * @returns {Promise<Object>} - Object with data and error properties
 */
export const saveProject = async (projectData) => {
  try {
    const user = await getCurrentUser();
    if (!user) return { data: null, error: 'User not authenticated' };

    const { data, error } = await supabase
      .from('projects')
      .insert([{
        user_id: user.id,
        name: projectData.name || 'Untitled Project',
        image_url: projectData.imageUrl,
        boxes: projectData.boxes || [],
        shapes: projectData.shapes || [],
        entry_points: projectData.entryPoints || [],
        scale: projectData.scale || { pixelsPerFoot: 30, referenceLine: null },
        ceiling_height: projectData.ceilingHeight || 9.0,
        customer: projectData.customer || null,
        location: projectData.location || null,
        installation_requirements: projectData.installationRequirements || {},
        archived: false
      }])
      .select();
    
    return { data: data?.[0] || null, error };
  } catch (error) {
    console.error('Error saving project:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Update a project
 * @param {string} projectId - Project ID
 * @param {Object} updates - Project data to update
 * @returns {Promise<Object>} - Object with data and error properties
 */
export const updateProject = async (projectId, updates) => {
  try {
    const user = await getCurrentUser();
    if (!user) return { data: null, error: 'User not authenticated' };

    // Create the update object with only the fields that are present in updates
    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.image_url !== undefined) updateData.image_url = updates.image_url;
    if (updates.imageUrl !== undefined) updateData.image_url = updates.imageUrl;
    if (updates.boxes !== undefined) updateData.boxes = updates.boxes;
    if (updates.shapes !== undefined) updateData.shapes = updates.shapes;
    if (updates.entry_points !== undefined) updateData.entry_points = updates.entry_points;
    if (updates.entryPoints !== undefined) updateData.entry_points = updates.entryPoints;
    if (updates.scale !== undefined) updateData.scale = updates.scale;
    if (updates.ceiling_height !== undefined) updateData.ceiling_height = updates.ceiling_height;
    if (updates.ceilingHeight !== undefined) updateData.ceiling_height = updates.ceilingHeight;
    if (updates.archived !== undefined) updateData.archived = updates.archived;
    if (updates.customer !== undefined) updateData.customer = updates.customer;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.installation_requirements !== undefined) updateData.installation_requirements = updates.installation_requirements;
    if (updates.installationRequirements !== undefined) updateData.installation_requirements = updates.installationRequirements;
    
    // Always update the updated_at timestamp
    updateData.updated_at = new Date().toISOString();

    console.log('Updating project with data:', updateData);

    const { data, error } = await supabase
      .from('projects')
      .update(updateData)
      .eq('id', projectId)
      .eq('user_id', user.id)
      .select();
    
    if (error) {
      console.error('Supabase update error:', error);
      return { data: null, error: error.message };
    }
    
    return { data: data?.[0] || null, error: null };
  } catch (error) {
    console.error('Error updating project:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Delete a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Object with error property
 */
export const deleteProject = async (projectId) => {
  try {
    const user = await getCurrentUser();
    if (!user) return { error: 'User not authenticated' };

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)
      .eq('user_id', user.id);
    
    return { error };
  } catch (error) {
    console.error('Error deleting project:', error);
    return { error: error.message };
  }
}; 